import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../shared/async/PromiseCompletionSource";
import { Viewable } from "../../../../scene/viewable/Viewable";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { IFirstTimeExperienceController } from "../../common/controller/IFirstTimeExperienceController";
import { IFirstTimeExperienceModel } from "../../common/models/IFirstTimeExperienceModel";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../common/resources/SoundsResourcePackageWrapper";
import { TopPanel } from "../../game/components/toppanel/TopPanel";
import { IRaidController } from "./controllers/IRaidController";
import { RaidResourcePackage } from "./resources/RaidResourcePackage";
import { RaidPopupView } from "./view/RaidPopupView";
import { RaidTutorialResourcePackage } from "./resources/RaidTutorialResourcePackage";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { UserProfileResourcePackage } from "../userprofile/resources/UserProfileResourcePackage";
import { RaidAction } from "../../game/flow/impl/diceActions/raidAction/RaidAction";
import { NoCacheDynamicPackage } from "../../common/resources/NoCacheDynamicPackage";

@injectable()
export class RaidPopupScene extends Viewable<void> {
    private readonly raidResources: RaidResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly raidController: IRaidController;
    private readonly canvasScaler: ICanvasScaler;
    private readonly action: RaidAction;
    private readonly topPanel: TopPanel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly firstTimeExperienceController: IFirstTimeExperienceController;
    private readonly logger: ILogger;
    private isFirstShow: boolean;
    private tutorialResources: RaidTutorialResourcePackage;
    private readonly raidDynamicResources: NoCacheDynamicPackage;

    public constructor(
        raidResources: RaidResourcePackage,
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper,
        userProfileResources: UserProfileResourcePackage,
        @inject("IRaidController") raidController: IRaidController,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        action: RaidAction,
        topPanel: TopPanel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("IFirstTimeExperienceController") firstTimeExperienceController: IFirstTimeExperienceController,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.raidResources = raidResources;
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.userProfileResources = userProfileResources;
        this.raidController = raidController;
        this.canvasScaler = canvasScaler;
        this.action = action;
        this.isFirstShow = false;
        this.topPanel = topPanel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.firstTimeExperienceController = firstTimeExperienceController;
        this.logger = logger;
        this.raidDynamicResources = new NoCacheDynamicPackage("raid");
    }

    @initializer()
    public async initialization(): Promise<void> {
        await this.asyncOperationNotification.show();
        try {
            this.topPanel.interactiveChildren = false;
            await this.loadRaidDynamicResources();
            if (this.firstTimeExperienceModel.tooltipsInfo.value.raid) {
                await this.firstTimeExperienceController.sendShownTooltip({ tooltips: { raid: false } });
                this.isFirstShow = true;
                this.tutorialResources = new RaidTutorialResourcePackage();
                await this.tutorialResources.load();
            }
            this.asyncOperationNotification.hide();
            const pcs = new PromiseCompletionSource<void>();
            this.view = new RaidPopupView(
                this.commonResources,
                this.raidResources,
                this.tutorialResources,
                this.soundResources,
                this.userProfileResources,
                this.raidController,
                this.canvasScaler,
                this.isFirstShow,
                this.raidDynamicResources,
                this.action,
                pcs,
                this.topPanel,
                this.asyncOperationNotification
            );
            pcs.result.then(() => {
                this.topPanel.interactiveChildren = true;
                this.operationControls.complete();
                this.tutorialResources?.destroy();
                this.userProfileResources.destroy();
                this.raidResources.destroy();
                this.raidDynamicResources.destroy();
            }, (reason: any) => {
                this.raidDynamicResources.destroy();
                this.topPanel.interactiveChildren = true;
                this.operationControls.error(reason);
            });
        }
        catch (e: unknown) {
            this.logger.error(e);
            this.asyncOperationNotification.hide();
            if (this.raidDynamicResources.loaded) {
                this.raidDynamicResources.destroy();
            }
            this.topPanel.interactiveChildren = true;
            this.operationControls.complete();
        }
    }

    private async loadRaidDynamicResources(): Promise<void> {
        this.raidDynamicResources.add(this.action.diceRollRaidCard.building.imageUrl);
        this.raidDynamicResources.add(this.action.diceRollRaidCard.guest.avatar);
        await this.raidDynamicResources.load();
    }
}
